import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Power, ShieldCheck, KeyRound, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AgentsPanel({ currentEmail }) {
  const [agents, setAgents] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // agent object

  const load = async () => {
    try { const { data } = await api.get("/admin/agents"); setAgents(data); }
    catch { setAgents([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setCreating(true);
    try {
      await api.post("/admin/agents", {
        name: f.get("name"), email: f.get("email"),
        password: f.get("password"), role: f.get("role") || "agent",
      });
      toast.success("Agent créé");
      e.target.reset();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/admin/agents/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Compte désactivé" : "Compte activé");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    const pass = new FormData(e.target).get("password");
    try {
      await api.post(`/admin/agents/${resetting.id}/reset-password`, { password: pass });
      toast.success(`Mot de passe réinitialisé pour ${resetting.name}`);
      setResetting(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const input = "w-full bg-background border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary";

  return (
    <div>
      {resetting && (
        <div className="dark fixed inset-0 z-[1100] grid place-items-center p-4 bg-black/70 backdrop-blur-sm">
          <form onSubmit={resetPassword} data-testid="reset-form"
            className="w-full max-w-md bg-card border border-border rounded-2xl p-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl text-foreground flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-secondary" /> Nouveau mot de passe
              </h3>
              <button type="button" data-testid="reset-close-btn" onClick={() => setResetting(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Pour <span className="text-foreground font-medium">{resetting.name}</span> ({resetting.email})
            </p>
            <input name="password" type="text" required minLength={6} autoFocus
              placeholder="Nouveau mot de passe (min. 6)" data-testid="reset-password-input"
              className="w-full bg-background border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary mb-5" />
            <button type="submit" data-testid="reset-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-secondary text-secondary-foreground font-medium hover:-translate-y-0.5 transition-transform">
              <KeyRound className="w-4 h-4" /> Réinitialiser
            </button>
          </form>
        </div>
      )}

      <div className="mb-6">
        <h1 className="font-heading text-4xl lg:text-5xl text-foreground">Agents de terrain</h1>
        <p className="text-muted-foreground mt-1">Créez des comptes agents, activez/désactivez l'accès et suivez qui saisit les parcelles.</p>
      </div>

      <form onSubmit={create} data-testid="agent-form"
        className="grid sm:grid-cols-5 gap-4 mb-8 bg-card border border-border rounded-xl p-5">
        <input name="name" required placeholder="Nom de l'agent" data-testid="agent-name" className={input + " sm:col-span-1"} />
        <input name="email" type="email" required placeholder="Email" data-testid="agent-email" className={input + " sm:col-span-1"} />
        <input name="password" type="text" required minLength={6} placeholder="Mot de passe (min. 6)" data-testid="agent-password" className={input + " sm:col-span-1"} />
        <select name="role" data-testid="agent-role" className={input + " sm:col-span-1"}>
          <option value="agent">Agent</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit" disabled={creating} data-testid="agent-create-btn"
          className="sm:col-span-1 inline-flex items-center justify-center gap-2 rounded-full bg-secondary text-secondary-foreground font-medium py-3 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Créer
        </button>
      </form>

      <div className="border border-border rounded-xl overflow-x-auto" data-testid="agents-list">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-card text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <div className="col-span-3">Nom</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Rôle</div>
            <div className="col-span-2">Parcelles saisies</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {agents === null ? (
            <div className="p-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-secondary" /></div>
          ) : agents.length === 0 ? (
            <div className="p-16 text-center text-muted-foreground">Aucun agent.</div>
          ) : (
            agents.map((a) => (
              <div key={a.id} data-testid="agent-row"
                className="grid grid-cols-12 gap-2 px-5 py-4 border-b border-border last:border-0 items-center text-sm">
                <div className="col-span-3 flex items-center gap-2">
                  {a.name}
                  {a.email === currentEmail && <span className="text-[10px] text-secondary">(vous)</span>}
                </div>
                <div className="col-span-3 text-muted-foreground truncate pr-2">{a.email}</div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
                    a.role === "admin" ? "text-secondary border-secondary/40 bg-secondary/10" : "text-muted-foreground border-border"
                  }`}>
                    {a.role === "admin" && <ShieldCheck className="w-3 h-3" />}{a.role}
                  </span>
                </div>
                <div className="col-span-2 font-mono text-secondary">{a.parcels_count}</div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button data-testid="agent-reset-btn" onClick={() => setResetting(a)} title="Réinitialiser le mot de passe"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-border text-muted-foreground hover:border-secondary hover:text-secondary transition-colors">
                    <KeyRound className="w-3 h-3" /> Mot de passe
                  </button>
                  <button data-testid="agent-toggle-btn" onClick={() => toggle(a)} disabled={a.email === currentEmail}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors disabled:opacity-40 ${
                      a.active ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10 hover:border-red-400 hover:text-red-400"
                               : "text-red-400 border-red-500/40 bg-red-500/10 hover:border-emerald-400 hover:text-emerald-400"
                    }`}>
                    <Power className="w-3 h-3" /> {a.active ? "Actif" : "Désactivé"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
